import { t } from '../i18n/t.js'

/**
 * Satz, Passwort oder PIN — drei Arten desselben Geheimnisses.
 *
 * ## Warum das keine drei Wege in den Speicher sind
 *
 * Kryptografisch ist alles hier **eine** Passphrase: derselbe PBKDF2-Pfad,
 * dasselbe Salz, derselbe `wrap:passphrase`-Eintrag im Tresor. Die Art
 * entscheidet nur, wie die Oberfläche fragt — Beschriftung, Tastatur,
 * Mindestlänge, Beispieltext.
 *
 * Das ist Absicht. Ein eigener Tresoreintrag je Art hieße: Wer heute von „Satz"
 * auf „PIN" wechselt, hätte hinterher **zwei** Türen, und die alte, vergessene
 * ginge weiter auf. Genau das darf nicht sein. Ein Wechsel überschreibt.
 *
 * ## Die Mindestlängen sind nicht gleich streng — mit Absicht
 *
 * Eine PIN mit zwölf Stellen tippt niemand, ein Satz mit sechs Zeichen ist
 * keiner. Die Zahlen unten sind der Punkt, an dem die jeweilige Art gerade noch
 * ihren Zweck erfüllt:
 *
 *   Satz      12  vier kurze Wörter, das Maß aus ADR-008
 *   Passwort   8  die übliche Untergrenze, wenn Zeichenarten gemischt sind
 *   PIN        6  vier Stellen sind zehntausend Versuche — offline nichts
 *
 * **Und der ehrliche Satz dazu:** Auch sechs Stellen sind nur eine Million
 * Möglichkeiten. Wer eine Kopie des Browserprofils in die Hände bekommt,
 * probiert sie durch. Die PIN ist der bequemste und der schwächste der drei
 * Wege, und die Oberfläche sagt das an der Stelle, an der man sie wählt —
 * nicht in einer Fußnote.
 */

export type GeheimnisArt = 'satz' | 'passwort' | 'pin'

/** In der Reihenfolge, in der die Oberfläche sie anbietet: stärkste zuerst. */
export const ARTEN: readonly GeheimnisArt[] = ['satz', 'passwort', 'pin'] as const

/**
 * Was in einem alten Speicher gilt, in dem noch keine Art hinterlegt ist.
 * Bis Phase 19 gab es nur den Satz — also ist es einer.
 */
export const STANDARD_ART: GeheimnisArt = 'satz'

const MINDESTENS: Record<GeheimnisArt, number> = {
  satz: 12,
  passwort: 8,
  pin: 6,
}

export function istArt(wert: unknown): wert is GeheimnisArt {
  return typeof wert === 'string' && (ARTEN as readonly string[]).includes(wert)
}

export function mindestlaenge(art: GeheimnisArt): number {
  return MINDESTENS[art]
}

/** Der Name der Art, wie er in einer Aufzählung steht. */
export function artName(art: GeheimnisArt): string {
  if (art === 'satz') return t('Satz')
  if (art === 'passwort') return t('Passwort')
  return t('PIN')
}

/** Die Beschriftung des Eingabefelds. */
export function feldName(art: GeheimnisArt, neu: boolean): string {
  if (art === 'satz') return neu ? t('Dein Satz') : t('Satz')
  if (art === 'passwort') return neu ? t('Dein Passwort') : t('Passwort')
  return neu ? t('Deine PIN') : t('PIN')
}

export function platzhalter(art: GeheimnisArt): string {
  if (art === 'satz') return t('zum Beispiel: mein blauer Stuhl knarzt')
  if (art === 'passwort') return t('mindestens 8 Zeichen')
  return t('mindestens 6 Ziffern')
}

/** Ein Satz darüber, wofür sich die Art eignet — steht neben der Wahl. */
export function erklaerung(art: GeheimnisArt): string {
  if (art === 'satz') {
    return t('Vier Wörter, die nur du in dieser Reihenfolge kennst. Am sichersten und am leichtesten zu merken.')
  }
  if (art === 'passwort') {
    return t('Ein gewöhnliches Passwort. Sicher genug, wenn es lang ist und nirgends sonst benutzt wird.')
  }
  return t('Nur Ziffern, schnell einzutippen. Der bequemste und zugleich der schwächste Weg.')
}

/**
 * Die Tastatur, die das Telefon aufklappt.
 *
 * Bei einer PIN ist das kein Schönheitsfehler: Wer die Zifferntastatur nicht
 * bekommt, sucht auf einem kleinen Bildschirm die Zahlenreihe.
 */
export function tastatur(art: GeheimnisArt): 'numeric' | 'text' {
  return art === 'pin' ? 'numeric' : 'text'
}

/**
 * Prüft eine Eingabe gegen die Regeln ihrer Art.
 *
 * Rückgabe: der Fehlertext, oder `null`, wenn alles stimmt. Die Texte nennen
 * immer die Zahl — „zu kurz" allein lässt jemanden raten.
 */
export function pruefen(art: GeheimnisArt, eingabe: string): string | null {
  const min = MINDESTENS[art]

  if (art === 'pin') {
    if (!/^\d*$/.test(eingabe)) return t('Eine PIN besteht nur aus Ziffern.')
    if (eingabe.length < min) {
      return t('Bitte mindestens {n} Ziffern.', { n: min })
    }
    if (/^(\d)\1*$/.test(eingabe)) {
      return t('Alle Ziffern gleich — das errät jemand sofort.')
    }
    if (aufsteigend(eingabe)) {
      return t('Eine Zahlenreihe wie diese errät jemand sofort.')
    }
    return null
  }

  if (eingabe.length < min) {
    return art === 'satz'
      ? t('Bitte mindestens {n} Zeichen — ein kurzer Satz funktioniert gut.', { n: min })
      : t('Bitte mindestens {n} Zeichen.', { n: min })
  }
  return null
}

/** „123456" und „654321" — beide Richtungen, sonst fängt die Prüfung nur eine. */
function aufsteigend(pin: string): boolean {
  let hoch = true
  let runter = true
  for (let i = 1; i < pin.length; i++) {
    const d = pin.charCodeAt(i) - pin.charCodeAt(i - 1)
    if (d !== 1) hoch = false
    if (d !== -1) runter = false
  }
  return hoch || runter
}
